import { useState, useEffect } from 'react';
import { IAutopark, IAutoparkCar, AutoparkService } from '../../services/autopark.service';
import {
  CarService,
  ICarUpdateRequest,
  IOsagoUpdateRequest,
  ICarPhotosUpdateRequest,
  ICreateCarRequest,
} from '../../services/car.service';
import PhotoModal from './PhotoModal';
import Pagination from '../Pagination/Pagination';
import CarTableRow from './CarTableRow';
import AddCarModal from '../Modal/AddCarModal';
import { useAppSelector } from '../../store/hooks';
import './autoparkCars.css';
import { toBackendDate } from '../../utils/utils';

interface AutoparkCarsProps {
  autopark: IAutopark;
}

function AutoparkCars({ autopark }: AutoparkCarsProps) {
  const user = useAppSelector((state) => state.user.user);
  const [expandedCarId, setExpandedCarId] = useState<string | null>(null);
  const [editingCarId, setEditingCarId] = useState<string | null>(null);
  const [editingCarData, setEditingCarData] = useState<Partial<IAutoparkCar>>({});
  const [filterCount] = useState(1);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedCarPhotos, setSelectedCarPhotos] = useState<Array<{ id: string; photoUrl: string }>>([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [selectedCarNumber, setSelectedCarNumber] = useState('');
  const [isAddCarModalOpen, setIsAddCarModalOpen] = useState(false);

  // Новые состояния для API
  const [cars, setCars] = useState<IAutoparkCar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  // Загрузка машин при монтировании компонента
  useEffect(() => {
    fetchCars();
  }, [autopark.id, currentPage]);

  const fetchCars = async () => {
    if (!autopark.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await AutoparkService.getAutoparkCars(autopark.id, currentPage, limit);
      setCars(response.data);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке машин');
      console.error('Ошибка при загрузке машин:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCarToggle = (carId: string) => {
    setExpandedCarId(expandedCarId === carId ? null : carId);
  };

  const handleEditToggle = (car: IAutoparkCar) => {
    if (editingCarId === car.id) {
      // Выход из режима редактирования без сохранения
      setEditingCarId(null);
      setEditingCarData({});
    } else {
      // Вход в режим редактирования - сохраняем поля для API и дополнительные поля для UI
      setEditingCarId(car.id);
      setExpandedCarId(car.id);
      setEditingCarData({
        // Поля для API PUT /cars/{id}
        number: car.number,
        mark: car.mark,
        model: car.model,
        taxiLicense: car.taxiLicense,
        issueDate: car.issueDate,
        validityPeriodOfDC: car.validityPeriodOfDC,
        diagnosticCardNumber: car.diagnosticCardNumber,
        // Дополнительные поля (пока недоступны через эту ручку API)
        validityPeriodOfOsago: car.validityPeriodOfOsago,
        osagoNumber: car.osagoNumber,
      });
    }
  };

  const handleSaveChanges = async (carId: string) => {
    try {
      // Подготавливаем данные для отправки - только поля, которые есть в API PUT /cars/{id}
      const updateData: ICarUpdateRequest = {
        number: editingCarData.number || '',
        mark: editingCarData.mark || '',
        model: editingCarData.model || '',
        taxiLicense: editingCarData.taxiLicense || '',
        issueDate: editingCarData.issueDate || '',
        validityPeriodOfDC: editingCarData.validityPeriodOfDC || '',
        diagnosticCardNumber: editingCarData.diagnosticCardNumber || 0,
      };

      // Подготавливаем данные ОСАГО для API PUT /cars/{id}/osago-details
      const osagoData: IOsagoUpdateRequest | {} =
        editingCarData.osagoNumber && editingCarData.validityPeriodOfOsago
          ? {
              osagoNumber: editingCarData.osagoNumber,
              validityPeriodOfOsago: editingCarData.validityPeriodOfOsago,
            }
          : editingCarData.osagoNumber
            ? {
                osagoNumber: editingCarData.osagoNumber,
              }
            : editingCarData.validityPeriodOfOsago
              ? {
                  validityPeriodOfOsago: editingCarData.validityPeriodOfOsago,
                }
              : {};

      // Отправляем основные данные автомобиля
      await CarService.updateCar(carId, updateData);

      // Отправляем данные ОСАГО
      if (Object.keys(osagoData).length > 0) {
        await CarService.updateCarOsago(carId, osagoData as IOsagoUpdateRequest);
      }

      // Обновляем список автомобилей
      await fetchCars();

      setEditingCarId(null);
      setEditingCarData({});
    } catch (error) {
      console.error('Ошибка при сохранении изменений:', error);
    }
  };

  const handleFieldChange = (field: keyof IAutoparkCar, value: string | number) => {
    setEditingCarData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const removeFilter = () => {
    console.log('Удалить фильтр');
  };

  const handlePhotoClick = (
    carImages: Array<{ id: string; photoUrl: string }>,
    photoIndex: number,
    carNumber: string,
  ) => {
    setSelectedCarPhotos(carImages.map((img) => ({ id: img.id, photoUrl: img.photoUrl })));
    setSelectedPhotoIndex(photoIndex);
    setSelectedCarNumber(carNumber);
    setIsPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setIsPhotoModalOpen(false);
    setSelectedCarPhotos([]);
    setSelectedPhotoIndex(0);
    setSelectedCarNumber('');
  };

  const handlePhotoUpload = async (carId: string, photoType: string, file: File) => {
    try {
      if (photoType === 'diagnostic-card') {
        await CarService.updateCarDiagnosticCardPhoto(carId, file);
      } else if (photoType === 'osago') {
        await CarService.updateCarOsagoPhoto(carId, file);
      } else if (photoType.startsWith('body-photo-')) {
        // Обработка фотографий кузова (body-photo-0, body-photo-1, и т.д.)
        const photoIndex = parseInt(photoType.split('-')[2]);
        const photoPositions = ['front', 'back', 'left', 'right', 'trunk'];
        const photoPosition = photoPositions[photoIndex];

        if (photoPosition) {
          const photos: ICarPhotosUpdateRequest = {
            [photoPosition]: file,
          } as ICarPhotosUpdateRequest;

          await CarService.updateCarPhotos(carId, photos);
        } else {
          console.warn(`Неизвестный индекс фото кузова: ${photoIndex}`);
          return;
        }
      } else {
        console.warn(`Неизвестный тип фото: ${photoType}`);
        return;
      }

      // Обновляем список автомобилей после загрузки фото
      await fetchCars();
    } catch (error: any) {
      console.error(`Ошибка при загрузке фото ${photoType}:`, error);
      throw error;
    }
  };

  const handleAddCarClick = () => {
    setIsAddCarModalOpen(true);
  };

  const handleAddCarModalClose = () => {
    setIsAddCarModalOpen(false);
  };

  const handleAddCarConfirm = async (carData: ICreateCarRequest) => {
    try {
      const newCar = await CarService.createCar(carData);

      // Добавляем новую машину в начало списка вместо перезапроса всех машин
      setCars((prevCars) => [newCar as unknown as IAutoparkCar, ...prevCars]);

      // Увеличиваем общий счетчик
      setTotalCount((prevCount) => prevCount + 1);

      // Если нужно, пересчитываем количество страниц
      setTotalPages(Math.ceil((totalCount + 1) / limit));
    } catch (error: any) {
      console.error('Ошибка при создании автомобиля:', error);
      throw error;
    }
  };

  // Обработка состояний загрузки и ошибок
  if (loading) {
    return (
      <div className="autopark-cars">
        <div className="autopark-cars__loading">Загрузка машин...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="autopark-cars">
        <div className="autopark-cars__error">
          <p>Ошибка: {error}</p>
          <button onClick={fetchCars} className="retry-button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="autopark-cars">
      {/* Заголовок */}
      <div className="autopark-cars__header">
        <h2 className="autopark-cars__title">Автомобили автопарка </h2>
        {user?.role === 'autopark_employee' && (
          <button className="add-car-button" onClick={handleAddCarClick}>
            Добавить
          </button>
        )}
      </div>

      {/* Заголовки таблицы */}
      <div className="cars-table-header">
        <span className="header-cell header-cell--number">Гос номер</span>
        <span className="header-cell header-cell--mark">Марка / Модель</span>
        <span className="header-cell header-cell--license">Лицензия на автомобиль</span>
        <span className="header-cell header-cell--documents">Документы и фото кузова</span>
      </div>

      {/* Список автомобилей */}
      <div className="cars-list">
        {cars.map((car) => (
          <CarTableRow
            key={car.id}
            car={car}
            expandedCarId={expandedCarId}
            editingCarId={editingCarId}
            editingCarData={editingCarData}
            onCarToggle={handleCarToggle}
            onEditToggle={handleEditToggle}
            onSaveChanges={handleSaveChanges}
            onFieldChange={handleFieldChange}
            onPhotoClick={handlePhotoClick}
            onPhotoUpload={handlePhotoUpload}
          />
        ))}
      </div>

      {/* Пагинация */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Модальное окно для просмотра фото */}
      <PhotoModal
        photos={selectedCarPhotos}
        isOpen={isPhotoModalOpen}
        onClose={closePhotoModal}
        initialPhotoIndex={selectedPhotoIndex}
        carNumber={selectedCarNumber}
      />

      {/* Модальное окно для добавления автомобиля */}
      <AddCarModal
        isOpen={isAddCarModalOpen}
        onClose={handleAddCarModalClose}
        onConfirm={handleAddCarConfirm}
        autoparkID={autopark.id}
      />
    </div>
  );
}

export default AutoparkCars;
